// Every environment-dependent value below comes from that environment's env
// file (`env/.env.<APP_ENV>`, shared ones in `env/.env`) — no fallbacks here.

export const STRAPI_URL = process.env.NEXT_PUBLIC_STRAPI_URL;
export const STRAPI_INTERNAL_URL = process.env.STRAPI_INTERNAL_URL;
export const TOKEN = process.env.NEXT_PUBLIC_STRAPI_API_TOKEN;
export const CF_ACCESS_CLIENT_ID = process.env.CF_ACCESS_CLIENT_ID;
export const CF_ACCESS_CLIENT_SECRET = process.env.CF_ACCESS_CLIENT_SECRET;

export const HUB_DOCUMENT_ID = process.env.NEXT_PUBLIC_HUB_DOCUMENT_ID;

export const BASE_URL = process.env.NEXT_PUBLIC_BASE_URL;

// OAuth2 + PKCE SSO (@picsart/runtime v3). gen.ai is a standalone domain, so it
// cannot reach the `.picsart.com` refresh cookie — v3 dropped the `authOverrides`
// same-origin proxy in favour of this flow.
//
// The three SSO values mirror miniapp-shell's `api/src/consts.ts`, which is the
// canonical standalone-domain SSO integration (PIA-287155):
//   - the authorization-server base is env-aware so staging bundles never point
//     at the production AS;
//   - `auth-service-StandaloneminiappsOAuth2Clinet` is the SSO team's shared
//     client for standalone miniapp domains (the misspelling is in the real
//     registered value — do not "fix" it). A gen.ai-specific client id, if one
//     is ever registered, overrides it via env;
//   - the scope is `openid user-global`, NOT the runtime's own `openid profile`
//     default — the shared client is registered for the former.
export const API_URL = process.env.NEXT_PUBLIC_API_URL;

/**
 * The app-level token Picsart APIs require on every browser request
 * (`x-app-authorization`). Public by design — it identifies the app, not a
 * user — and the runtime sends the same value on the requests it patches.
 */
export const APP_AUTHORIZATION_TOKEN = process.env.NEXT_PUBLIC_X_APP_AUTHORIZATION_TOKEN ?? '';
export const ADYEN_KEY = process.env.ADYEN_KEY;
// Monetization's last-resort Stripe key: it prefers the one `/stripe/payment-init`
// returns, and throws when neither is there.
export const STRIPE_KEY = process.env.STRIPE_KEY;
/**
 * Which deployment this is — readable in the browser, which is the whole point.
 *
 * `NEXT_PUBLIC_APP_ENV` comes first and must stay first. Next inlines only
 * `NEXT_PUBLIC_*` into the client bundle, so a bare `APP_ENV` is `undefined`
 * there however carefully the build sets it, and every client-side reader fell
 * through to `NODE_ENV` — which `next build` pins to `production` for *every*
 * deployment, stage and preprod included.
 *
 * That is why sign-in worked locally and not once deployed: `next dev` leaves
 * `NODE_ENV` as `development`, so localhost picked the stage authorization
 * server, while the stage build looked like production to its own client code
 * and sent people to `auth.picsart.com` — which does not carry the stage
 * redirect URIs. `DatadogScript` reads this too, so stage RUM was being
 * reported as production for the same reason.
 *
 * Every env file sets it, so there is nothing to fall back to — and falling
 * back to `NODE_ENV` is exactly the bug above.
 */
export const APP_ENV = process.env.NEXT_PUBLIC_APP_ENV;
export const IS_CACHE_ENABLED = process.env.NEXT_PUBLIC_CACHE_ENABLED === 'true';
export const IS_PRODUCTION = APP_ENV === 'production';

// The authorization server, per environment — stage and preprod on
// `auth-stage.picsartstage2.com`, production on `auth.picsart.com`.
export const SSO_AUTH_URL = process.env.NEXT_PUBLIC_SSO_AUTH_URL;
// The SSO team's shared client for standalone miniapp domains, same default as
// miniapp-shell's `SSO_CLIENT_ID`. The misspelling is in the real registered
// value — do not "fix" it.
//
// Verified against auth-stage on 2026-09-09 by issuing the same
// `/oauth2/authorize` request under each client id: this one returns the Picsart
// sign-in page, while `auth-service-gen-ai-web` answers
// `error=invalid_request … OAuth 2.0 Parameter: redirect_uri` for every
// redirect_uri tried (with and without a trailing slash). A gen.ai-specific
// client can take over via env once it is actually registered with the
// authorization server.
//
// Not required: blank turns SSO off (`buildSsoConfig` returns `undefined`,
// and `RuntimeProvider` warns).
export const SSO_CLIENT_ID = process.env.NEXT_PUBLIC_SSO_CLIENT_ID;
export const SSO_SCOPE = process.env.NEXT_PUBLIC_SSO_SCOPE;

// Monetization + growth-rc HttpService base. Deliberately NOT `API_URL`: on
// stage that carries a gateway path (`/gw-v2`) which these clients append their
// own paths to, so they 404. miniapp-shell passes the monetization URL here for
// the same reason (and `miniapp/federation.ts` strips the path from API_URL for
// the same underlying reason).
export const MONETIZATION_API_URL = process.env.NEXT_PUBLIC_MONETIZATION_API_URL;

// Pulse analytics environment. Passed explicitly rather than left to @pulse's
// hostname inference: inference only recognises `picsartstage2`/`stage`-marked
// hosts, so a dedicated domain like `stage.gen.ai` reads as production and
// staging analytics land in the prod backend.
export const PULSE_ENV = process.env.NEXT_PUBLIC_PULSE_ENV;
